import React from 'react';
import {
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import { GapStylingConfig } from './types';

interface AdvancedTabProps {
  config: GapStylingConfig;
  onNestedConfigChange: <T extends keyof GapStylingConfig>(
    key: T,
    updates: Partial<GapStylingConfig[T]>
  ) => void;
}

/**
 * Advanced tab component for gap styling configuration
 * Handles margins and animation settings
 */
const AdvancedTab: React.FC<AdvancedTabProps> = ({
  config,
  onNestedConfigChange
}) => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">Margins</Typography>
      </Grid>
      <Grid item xs={3}>
        <TextField
          fullWidth
          type="number"
          label="Top"
          value={config.margin.top}
          onChange={(e) => onNestedConfigChange('margin', { top: Number(e.target.value) })}
        />
      </Grid>
      <Grid item xs={3}>
        <TextField
          fullWidth
          type="number"
          label="Right"
          value={config.margin.right}
          onChange={(e) => onNestedConfigChange('margin', { right: Number(e.target.value) })}
        />
      </Grid>
      <Grid item xs={3}>
        <TextField
          fullWidth
          type="number"
          label="Bottom"
          value={config.margin.bottom}
          onChange={(e) => onNestedConfigChange('margin', { bottom: Number(e.target.value) })}
        />
      </Grid>
      <Grid item xs={3}>
        <TextField
          fullWidth
          type="number"
          label="Left"
          value={config.margin.left}
          onChange={(e) => onNestedConfigChange('margin', { left: Number(e.target.value) })}
        />
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <FormControlLabel
          control={
            <Switch
              checked={config.animation.enabled}
              onChange={(e) => onNestedConfigChange('animation', { enabled: e.target.checked })}
            />
          }
          label="Enable Animation"
        />
      </Grid>

      {config.animation.enabled && (
        <>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Animation Type</InputLabel>
              <Select
                value={config.animation.type}
                onChange={(e) => onNestedConfigChange('animation', { type: e.target.value as GapStylingConfig['animation']['type'] })}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="pulse">Pulse</MenuItem>
                <MenuItem value="glow">Glow</MenuItem>
                <MenuItem value="slide">Slide</MenuItem>
                <MenuItem value="fade">Fade</MenuItem>
                <MenuItem value="bounce">Bounce</MenuItem>
                <MenuItem value="rotate">Rotate</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Duration (seconds)"
              value={config.animation.duration}
              onChange={(e) => onNestedConfigChange('animation', { duration: Number(e.target.value) })}
              inputProps={{ min: 0.1, step: 0.1 }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.animation.infinite}
                  onChange={(e) => onNestedConfigChange('animation', { infinite: e.target.checked })}
                />
              }
              label="Infinite Animation"
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default AdvancedTab;